import React, { useContext, useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Typography, Box, Paper, useTheme } from "@material-ui/core";
import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";
import { getBackendUrl } from "../../config";
import logo from "../../assets/logo.png";
import logoDark from "../../assets/logo-black.png";

const useStyles = makeStyles((theme) => ({
	container: {
		display: "flex",
		flexDirection: "column",
		justifyContent: "center",
		alignItems: "center",
		height: "100%",
		width: "100%",
		background: theme.palette.background.default,
		padding: theme.spacing(4),
		textAlign: "center",
		overflow: "hidden",
		position: "relative",
	},
	backgroundDecoration: {
		position: "absolute",
		width: "400px",
		height: "400px",
		borderRadius: "50%",
		background: "linear-gradient(135deg, rgba(101, 23, 171, 0.1) 0%, rgba(142, 68, 173, 0.05) 100%)",
		filter: "blur(60px)",
		top: "-100px",
		right: "-100px",
		zIndex: 0,
	},
	backgroundDecoration2: {
		position: "absolute",
		width: "300px",
		height: "300px",
		borderRadius: "50%",
		background: "linear-gradient(135deg, rgba(101, 23, 171, 0.08) 0%, rgba(142, 68, 173, 0.03) 100%)",
		filter: "blur(50px)",
		bottom: "-50px",
		left: "-50px",
		zIndex: 0,
	},
	glassCard: {
		position: "relative",
		zIndex: 1,
		padding: theme.spacing(6),
		borderRadius: "24px",
		background: theme.palette.type === "light" 
			? "rgba(255, 255, 255, 0.7)" 
			: "rgba(30, 41, 59, 0.4)",
		backdropFilter: "blur(20px)",
		WebkitBackdropFilter: "blur(20px)",
		border: theme.palette.type === "light" 
			? "1px solid rgba(255, 255, 255, 0.5)" 
			: "1px solid rgba(255, 255, 255, 0.05)",
		boxShadow: theme.palette.type === "light"
			? "0 20px 40px rgba(0, 0, 0, 0.05)"
			: "0 20px 40px rgba(0, 0, 0, 0.3)",
		maxWidth: "600px",
		width: "100%",
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		animation: "$fadeInUp 0.8s ease-out forwards",
	},
	logoWrapper: {
		marginBottom: theme.spacing(3),
		animation: "$logoFloat 4s infinite ease-in-out",
	},
	logo: {
		maxWidth: "180px",
		height: "auto",
	},
	greeting: {
		fontSize: "2.25rem",
		fontWeight: 800,
		color: theme.palette.type === "light" ? "#1e1b4b" : "#f8fafc",
		marginBottom: theme.spacing(1),
		fontFamily: "'Inter', sans-serif",
	},
	userName: {
		color: "#6517ab", // Orion Purple
		background: "linear-gradient(90deg, #6517ab 0%, #8e44ad 100%)",
		WebkitBackgroundClip: "text",
		WebkitTextFillColor: "transparent",
	},
	subtitle: {
		fontSize: "1.1rem",
		fontWeight: 500,
		color: theme.palette.type === "light" ? "#64748b" : "#94a3b8",
		marginBottom: theme.spacing(4),
	},
	divider: {
		width: "80px",
		height: "4px",
		background: "linear-gradient(90deg, #6517ab 0%, #8e44ad 100%)",
		borderRadius: "2px",
		marginBottom: theme.spacing(4),
	},
	instructionBox: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(1.5),
		padding: theme.spacing(2, 3),
		borderRadius: "16px",
		background: theme.palette.type === "light" 
			? "rgba(101, 23, 171, 0.05)" 
			: "rgba(101, 23, 171, 0.1)",
		color: theme.palette.type === "light" ? "#4c1d95" : "#c084fc",
		fontWeight: 600,
		fontSize: "0.95rem",
	},
	"@keyframes fadeInUp": {
		"0%": { opacity: 0, transform: "translateY(30px) scale(0.95)" },
		"100%": { opacity: 1, transform: "translateY(0) scale(1)" },
	},
	"@keyframes logoFloat": {
		"0%, 100%": { transform: "translateY(0)" },
		"50%": { transform: "translateY(-10px)" },
	},
}));

const WelcomePage = ({ children }) => {
	const classes = useStyles();
	const theme = useTheme();
	const { user } = useContext(AuthContext);
	const [greeting, setGreeting] = useState("");

	useEffect(() => {
		const hour = new Date().getHours();
		if (hour >= 5 && hour < 12) setGreeting("Bom dia");
		else if (hour >= 12 && hour < 18) setGreeting("Boa tarde");
		else setGreeting("Boa noite");
	}, []);

	return (
		<Box className={classes.container}>
			<div className={classes.backgroundDecoration} />
			<div className={classes.backgroundDecoration2} />
			
			<Paper elevation={0} className={classes.glassCard}>
				<Box className={classes.logoWrapper}>
					<img 
						src={theme.palette.type === "light" ? theme.calculatedLogoLight() : theme.calculatedLogoDark()} 
						alt="Logo" 
						className={classes.logo}
					/>
				</Box>

				<Typography className={classes.greeting}>
					{greeting}, <span className={classes.userName}>{user?.name}</span>!
				</Typography>

				<Typography className={classes.subtitle}>
					Tudo pronto para começar seus atendimentos de hoje?
				</Typography>

				<div className={classes.divider} />

				<Box className={classes.instructionBox}>
					<span style={{ fontSize: "1.2rem" }}>👋</span>
					{i18n.t("chat.noTicketMessage")}
				</Box>

				{children && (
					<Box mt={4} width="100%">
						{children}
					</Box>
				)}
			</Paper>
		</Box>
	);
};

export default WelcomePage;
